import { useContext, useRef } from "react";
import Axios from "axios";

// Importación de contexto y hooks relacionados
import { ConfiguratorContext } from "../context";
import usePersonalData from "./usePersonalData";
import useDealershipsData from "./useDealershipsData";
import useConfiguratorData from "./useConfiguratorData";
import usePriceData, { computeFinancedTotal } from "./usePriceData";
import useApprovalHistoryData from "./useApprovalHistoryData";
import usePurchaseTimeframeData from "./usePurchaseTimeframeData";
import { hasCurrentMafSimulation } from "./useMAFSimulationData";
import { CurrencyFormat, cleanRutFormat } from "../../utils";

/**
 * Hook para manejo de leads - creación, actualización y formateo de datos
 * Compatible con v1 (financiamiento tradicional) y v2 (MAF/Kinto)
 */
const useLeadData = () => {
    const [state, setState] = useContext(ConfiguratorContext);

    // Extracción de funciones y datos de los hooks
    const { getSelectedVersion, getSelectedModel } = useConfiguratorData();
    const { getPriceWithBonusOnly } = usePriceData();
    const { getSelectedTimeframe, getTimeframeOptions } = usePurchaseTimeframeData();

    // Variables utilizadas frecuentemente
    const selectedModel = getSelectedModel();
    const selectedVersion = getSelectedVersion();
    const { getPersonalData, getPersonType, isCompany } = usePersonalData();
    const { selectedDealership } = useDealershipsData();
    const { getLastApprovalResponse } = useApprovalHistoryData();

    // Guards contra doble-click y race conditions
    const creatingLeadRef = useRef(false);
    const creatingKintoLeadRef = useRef(false);

    // El guard sólo se limpia cuando la creación falla. Si el estado del lead
    // de Kinto se descarta (cambio de persona a empresa, o resetKintoLeadState)
    // hay que liberarlo para permitir crear el lead del nuevo producto.
    if (!state.kinto_lead_created) {
        creatingKintoLeadRef.current = false;
    }

    // Referencia siempre apuntando al último estado. Los handlers de envío hacen
    // `await ensureSimulation()` antes de llamar a updateLead/getLeadData, y el
    // closure del render original no vería el resultado de esa simulación:
    // terminaría guardando los valores previos (de otra versión o vacíos).
    const stateRef = useRef(state);
    stateRef.current = state;

    // Determina qué valores de financiamiento usar según la versión y opción de pago
    const getFinancingValues = (current = stateRef.current) => {
        const appVersion = current.appVersion;
        const selectedPaymentOption = current.selectedPaymentOption;

        // Obtener datos de simulaciones
        const mafData = current.mafSimulationData;
        const kintoSimData = current.kintoSimulationData;
        const kintoOneData = current.kinto_one_personal;

        // En v2, determinar el tipo basado en la opción de pago seleccionada
        if (appVersion === "v2") {
            // Opción 2: Financiamiento MAF
            if (selectedPaymentOption === 2 && mafData) {
                return {
                    type: "maf",
                    downpayment_percentage: mafData.downpayment_percentage ? parseFloat(mafData.downpayment_percentage) : null,
                    number_fees: mafData.number_fees ? parseInt(mafData.number_fees) : null,
                    kilometers: null, // MAF no usa kilómetros
                };
            }

            // Opción 3: Kinto (alquiler mensual)
            if (selectedPaymentOption === 3) {
                let kintoFeeValue = null;
                const kintoAmortization = kintoSimData?.resultData?.amortization || kintoOneData?.api_response?.amortization;
                if (kintoAmortization && kintoAmortization.length > 0) {
                    const lastAmortization = kintoAmortization[kintoAmortization.length - 1];
                    kintoFeeValue = lastAmortization.totalQuota || null;
                }

                // Si hay una cuota simulada, las condiciones informadas son las de
                // esa misma simulación. Así la cuota, el plazo, el pie y el
                // kilometraje guardados siempre provienen de un único cálculo.
                const simulatedFor = kintoSimData?.simulated_for;
                const isSimulated = Boolean(
                    kintoFeeValue &&
                        simulatedFor &&
                        simulatedFor.version_id === current.selectedVersion?.id
                );

                const source = isSimulated
                    ? simulatedFor
                    : {
                          downpayment:
                              kintoOneData?.selected_downpayment ?? kintoSimData?.downpayment,
                          time: kintoOneData?.selected_months || kintoSimData?.time || null,
                          kilometers:
                              kintoOneData?.selected_kilometers || kintoSimData?.kilometers || null,
                      };

                return {
                    type: "kinto",
                    downpayment_percentage:
                        source.downpayment !== null && source.downpayment !== undefined
                            ? parseFloat(source.downpayment)
                            : null,
                    number_fees: source.time ? parseInt(source.time) : null,
                    kilometers: source.kilometers ? parseInt(source.kilometers) : null,
                    fee_value: isSimulated ? parseFloat(kintoFeeValue) : null,
                };
            }

            // Opción 1: Contado (no necesita valores de financiamiento)
            if (selectedPaymentOption === 1) {
                return {
                    type: "cash",
                    downpayment_percentage: null,
                    number_fees: null,
                    kilometers: null,
                };
            }
        }

        // En v1 o caso por defecto, usar el financiado tradicional
        const financiado = current.financiado;
        return {
            type: "traditional",
            downpayment_percentage: financiado?.downpayment_percentage ? parseFloat(financiado.downpayment_percentage) : null,
            number_fees: financiado?.number_fees ? parseInt(financiado.number_fees) : null,
            kilometers: null,
        };
    };

    // Obtiene datos completos de financiamiento según la versión (v1/v2) y tipo de pago
    const getFinancingData = (current = stateRef.current) => {
        const appVersion = current.appVersion;
        const selectedPaymentOption = current.selectedPaymentOption;

        if (appVersion === "v2") {
            // Para MAF (opción 2 en v2)
            if (selectedPaymentOption === 2) {
                const mafData = current.mafSimulationData;
                return {
                    isFinanced: true,
                    type: "maf",
                    data: mafData || {},
                    ignore: false,
                };
            }

            // Para Kinto (opción 3 en v2) - no usa financiamiento tradicional
            if (selectedPaymentOption === 3) {
                return {
                    isFinanced: false,
                    type: "kinto",
                    data: {},
                    ignore: true,
                };
            }

            // Para contado (opción 1 en v2)
            if (selectedPaymentOption === 1) {
                return {
                    isFinanced: false,
                    type: "cash",
                    data: {},
                    ignore: true,
                };
            }
        }

        // Fallback para v1: usar financiamiento tradicional
        const financiado = current.financiado;
        const ignoreFinanciado = current.financiado.ignore;

        return {
            isFinanced: !ignoreFinanciado.value,
            type: "traditional",
            data: financiado || {},
            ignore: ignoreFinanciado.value,
        };
    };

    // Crea el primer paso de lead (solo en v1)
    const createFirstStepLead = () => {
        Axios.post(`/api/vehicles/bnp/leads/create_first_step_lead/`, {
            ...getPersonalData(),
            model_name: selectedModel.name,
        }).then((response) => {
            if (response.status === 201) {
                setState((prevState) => ({
                    ...prevState,
                    first_step_lead: {
                        id: response.data.id_first_step_lead,
                    },
                }));
            }
        });
    };

    // Ejecuta createFirstStepLead solo si está en v1
    const createFirstStepLeadIfEnabled = () => {
        // Activar createFirstStepLead solo para v1
        if (state.appVersion === "v1") {
            createFirstStepLead();
        }
    };

    // Verifica si debe usar first step lead (solo v1)
    const shouldUseFirstStepLead = () => {
        return state.appVersion === "v1";
    };

    // Obtiene la versión actual de la aplicación
    const getAppVersion = () => {
        return state.appVersion;
    };

    const getType = () => getPersonType();

    // Crea el lead principal con todos los datos del configurador
    const createLead = () => {
        // Guard contra doble-click
        if (creatingLeadRef.current) return;

        // Validar que haya un concesionario seleccionado
        if (!selectedDealership || !selectedDealership.id) {
            console.error("No se puede crear el lead: no hay concesionario seleccionado");
            return;
        }

        creatingLeadRef.current = true;

        const firstStepLeadId =
            state.appVersion === "v1" ? state.first_step_lead?.id || null : null;
        const financing = getFinancingValues();

        const tiene_hibrido = selectedVersion.motor?.id == 2 ? "yes" : "no";

        if (dataLayer) {
            dataLayer.push({
                'event': 'Lead',
                'eventLabel': 'Comenzar',
                'tiene_hibrido': tiene_hibrido,
                'tipo_usuario': getType(),
                'modelo': selectedModel.name.replace(/\s+/g, '_').toLowerCase(),
            });

        }

        // Obtener datos personales y limpiar máscara del RUT antes de enviar
        const personalData = getPersonalData();
        const cleanRut = cleanRutFormat(personalData.rut);

        Axios.post(`/api/vehicles/bnp/leads/create_lead/`, {
            ...personalData,
            rut: cleanRut,
            dealership_id: selectedDealership?.id || null,
            model_id: selectedModel.id,
            dealer_brand_id: selectedDealership?.brand_id || null,
            id_first_step_lead: firstStepLeadId,
            data: [
                {
                    name: "modelo-texto",
                    value: selectedModel.name,
                },
                {
                    name: "gclid",
                    value: sessionStorage.getItem("gclid") || "",
                },
                {
                    name: "utm_source",
                    value: sessionStorage.getItem("utm_source") || "",
                },
                {
                    name: "utm_medium",
                    value: sessionStorage.getItem("utm_medium") || "",
                },
                {
                    name: "utm_campaign",
                    value: sessionStorage.getItem("utm_campaign") || "",
                },
                {
                    name: "cuota-mensual-kinto",
                    value: financing.type === "kinto" ? financing.fee_value : null,
                },
                {
                    name: "porcentaje-pie-kinto",
                    value: financing.type === "kinto" ? financing.downpayment_percentage : null,
                },
                {
                    name: "cantidad-cuotas-kinto",
                    value: financing.type === "kinto" ? financing.number_fees : null,
                },
                {
                    name: "kilometraje-kinto",
                    value: financing.type === "kinto" ? financing.kilometers : null,
                },
                {
                    name: "type",
                    value: getType(),
                },
                {
                    name: "name",
                    value: (() => {
                        const personalData = getPersonalData();
                        // Si es empresa, mostrar "Nombre Apellido - Razón Social"
                        if (
                            personalData?.type === "empresa" ||
                            (personalData?.company_name && personalData.company_name.trim() !== "")
                        ) {
                            const fullName = personalData?.full_name || "";
                            const companyName = personalData?.company_name || "";
                            if (fullName && companyName) {
                                return `${fullName} - ${companyName}`;
                            }
                            return companyName || fullName || "";
                        }
                        // Si es persona, usar solo full_name
                        return personalData?.full_name || "";
                    })(),
                },
            ],
        }).then((response) => {
            if (response.status === 201) {
                setState((prevState) => ({
                    ...prevState,
                    reservo: {
                        id_lead: response.data.id_lead,
                    },
                }));
            }
        }).finally(() => {
            creatingLeadRef.current = false;
        });
    };

    // Crea lead para vehículos sin stock disponible
    const createOutOfStockLead = (selectedVersion) => {
        // Validar que haya un concesionario seleccionado
        if (!selectedDealership || !selectedDealership.id) {
            console.error("No se puede crear el lead de out of stock: no hay concesionario seleccionado");
            return;
        }

        Axios.post(`/api/vehicles/bnp/leads/create_out_of_stock_lead/`, {
            ...getPersonalData(),
            dealership_id: selectedDealership?.id || null,
            version_id: selectedVersion.id,
            dealer_brand_id: selectedDealership?.brand_id || null,
        })
            .then()
            .catch((err) => {
                console.log(err);
            });
    };

    // Genera el array de datos del lead con toda la información de la configuración
    const getLeadData = () => {
        // Snapshot del estado más reciente (ver comentario en stateRef): todos los
        // campos del lead se derivan de este mismo snapshot, de forma que nunca
        // se mezclen datos de dos configuraciones distintas.
        const current = stateRef.current;
        const selectedModel = current.selectedModel;
        const selectedVersion = current.selectedVersion;
        const selectedColor = current.details.selectedColor;
        const addedAccessories = current.accessories.added || [];
        const prepaidMaintenancePlan = current.selectedPrepaidMaintenancePlan;
        const usado = current.usado;
        const finalPrice = computeFinancedTotal(current);

        const financing = getFinancingValues(current);
        const financingData = getFinancingData(current);

        // Sólo se guardan cifras de crédito respaldadas por una simulación vigente
        // para esta versión y este monto financiado. Si la simulación quedó
        // obsoleta se guarda null: nunca un valor de otra configuración.
        const mafResultsValid =
            financingData.type === "maf" && hasCurrentMafSimulation(current);

        let leadData = [
            {
                name: "modelo-texto",
                value: selectedModel.name,
            },
            {
                name: "version-texto",
                value: selectedVersion.name,
            },
            {
                name: "version-id",
                value: selectedVersion.id,
            },
            {
                name: "codigo-sap-vehiculo",
                value: selectedVersion.sap_id,
            },
            {
                name: "accesorios",
                value:
                    addedAccessories.length > 0
                        ? addedAccessories
                              .map((accessory) => `${accessory.name} (${accessory.price})`)
                              .join("/")
                        : null,
            },
            {
                name: "codigo-sap-accesorios-precio",
                value:
                    addedAccessories.length > 0
                        ? addedAccessories
                              .map((accessory) => `${accessory.sap_id}-${accessory.price}`)
                              .join("|")
                        : null,
            },
            {
                name: "precio-de-lista",
                value: selectedVersion.list_price,
            },
            {
                name: "precio",
                value: (() => {
                    // Determinar la opción de pago
                    let paymentOption = null;
                    if (current.appVersion === "v2") {
                        if (current.selectedPaymentOption === 1) paymentOption = "contado";
                        else if (current.selectedPaymentOption === 2) paymentOption = "financiado";
                        else if (current.selectedPaymentOption === 3) paymentOption = "kinto";
                    } else {
                        // v1: contado o financiado
                        paymentOption = current.financiado.ignore.value ? "contado" : "financiado";
                    }

                    // Calcular precio según la opción de pago
                    let price = parseFloat(selectedVersion.list_price || 0);

                    if (paymentOption === "kinto") {
                        // KINTO: No aplican bonos, usar list_price directamente
                        return price;
                    } else if (paymentOption === "contado") {
                        // Contado: Solo aplica bono marca
                        if (selectedVersion.brand_bonus) {
                            price = price - parseFloat(selectedVersion.brand_bonus);
                        }
                        return price;
                    } else if (paymentOption === "financiado") {
                        // Financiado: Aplica bono marca + bono financiamiento
                        if (selectedVersion.brand_bonus) {
                            price = price - parseFloat(selectedVersion.brand_bonus);
                        }
                        if (selectedVersion.financing_bonus) {
                            price = price - parseFloat(selectedVersion.financing_bonus);
                        }
                        // Plan Renueve se aplica solo si está activado
                        if (financingData.data.smart_buy && selectedVersion.smart_buy_bonus) {
                            price = price - parseFloat(selectedVersion.smart_buy_bonus);
                        }
                        return price;
                    }

                    return price;
                })(),
            },
            {
                name: "color",
                value: selectedColor?.name,
            },
            {
                name: "codigo-sap-color-vehiculo",
                value: selectedColor?.sap_id,
            },
            {
                name: "opcion-pago",
                value: (() => {
                    if (current.appVersion === "v2") {
                        if (current.selectedPaymentOption === 1) return "contado";
                        if (current.selectedPaymentOption === 2) return "financiado";
                        // Valor único para Kinto. El producto (One Personal /
                        // One Business) se resuelve por el tipo de cliente al
                        // crear el lead, no por este campo.
                        if (current.selectedPaymentOption === 3) return "kinto";
                    }
                    // Fallback para v1
                    return current.financiado.ignore.value ? "contado" : "financiado";
                })(),
            },
            {
                name: "plan-renueve",
                value: financingData.ignore ? null : financingData.data.smart_buy ? "Si" : "No",
            },
            {
                name: "porcentaje-pie",
                value: financingData.ignore ? null : financing.downpayment_percentage,
            },
            {
                name: "cantidad-cuotas",
                value: financingData.ignore ? null : financing.number_fees,
            },
            {
                name: "precio-final",
                value: finalPrice,
            },
            {
                name: "maf-simulation-id",
                value: mafResultsValid ? financingData.data.simulation_id || null : null,
            },
            {
                name: "cae",
                value:
                    financingData.ignore || (financingData.type === "maf" && !mafResultsValid)
                        ? null
                        : financingData.data.equivalent_annual_load,
            },
            {
                name: "costo-total-credito",
                value: (() => {
                    if (financingData.ignore) return null;
                    // En v2 con MAF, usar datos de MAF
                    if (financingData.type === "maf") {
                        return mafResultsValid
                            ? financingData.data.credit_total_cost || null
                            : null;
                    }
                    // En v1 o tradicional, usar los datos de la simulación tradicional
                    const simulatedData = current.simulated_data.simulated_credit_data || {};
                    return simulatedData.credit_total_cost ? simulatedData.credit_total_cost : null;
                })(),
            },
            {
                name: "cuota-mensual",
                value:
                    financingData.ignore || (financingData.type === "maf" && !mafResultsValid)
                        ? null
                        : financingData.data.fee_value,
            },
            {
                name: "cuota-final",
                value:
                    financingData.ignore || (financingData.type === "maf" && !mafResultsValid)
                        ? null
                        : financingData.data.smart_buy
                        ? financingData.data.future_value_guaranteed
                        : null,
            },
            {
                name: "ingreso",
                value: financingData.ignore
                    ? null
                    : financingData.data.salary
                    ? financingData.data.salary
                    : null,
            },
            {
                name: "resultado-preaprobacion",
                value: (() => {
                    if (
                        financingData.ignore ||
                        financingData.data.pre_approved === null ||
                        financingData.data.pre_approved === undefined
                    ) {
                        return null;
                    }
                    return financingData.data.pre_approved ? "aprobado" : "no aprobado";
                })(),
            },
            {
                name: "informacion_preaprobacion",
                value: getLastApprovalResponse(),
            },
            {
                name: "mantenciones-prepagadas",
                value: prepaidMaintenancePlan
                    ? `${prepaidMaintenancePlan.name} (${prepaidMaintenancePlan.price})`
                    : "No incluye MPP",
            },
            {
                name: "codigo-sap-mantencion-precio",
                value: prepaidMaintenancePlan
                    ? `${prepaidMaintenancePlan.sap_id}-${prepaidMaintenancePlan.price}`
                    : "No incluye MPP",
            },
            {
                name: "quiero-que-me-contacten",
                value: "No",
            },
            {
                name: "medio-de-contacto",
                value: null,
            },
            {
                name: "auto-usado",
                value: usado.range
                    ? `${usado.details.make} / ${usado.details.model} / ${
                          usado.selectedVersion.name
                      } / ${usado.details.year} / ${usado.mileage.name} / ${CurrencyFormat(
                          usado.range.min
                      )} a ${CurrencyFormat(usado.range.max)} / Mantenciones Oficiales: ${
                          usado.checkedMaintenance ? "Si" : "No"
                      } / ${usado.patent}`
                    : null,
            },
            {
                name: "proceso-compra",
                value: getSelectedTimeframe() ? getSelectedTimeframe().name : "Solo cotizando",
            },
            {
                name: "categoria-lead",
                value: "normal",
            },
            {
                name: "gclid",
                value: sessionStorage.getItem("gclid") || "",
            },
            {
                name: "utm_source",
                value: sessionStorage.getItem("utm_source") || "",
            },
            {
                name: "utm_medium",
                value: sessionStorage.getItem("utm_medium") || "",
            },
            {
                name: "utm_campaign",
                value: sessionStorage.getItem("utm_campaign") || "",
            },
            {
                name: "porcentaje-pie-kinto",
                value: financing.type === "kinto" ? financing.downpayment_percentage : null,
            },
            {
                name: "cuota-mensual-kinto",
                value: financing.type === "kinto" ? financing.fee_value : null,
            },
            {
                name: "cantidad-cuotas-kinto",
                value: financing.type === "kinto" ? financing.number_fees : null,
            },
            {
                name: "kilometraje-kinto",
                value: financing.type === "kinto" ? financing.kilometers : null,
            },
            {
                name: "type",
                value: (() => {
                    const personalData = getPersonalData();
                    // Si type está explícitamente definido, usarlo
                    if (personalData?.type && personalData.type !== "") {
                        return personalData.type;
                    }
                    // Si no está definido, inferir basado en company_name
                    if (personalData?.company_name && personalData.company_name.trim() !== "") {
                        return "empresa";
                    }
                    return "persona";
                })(),
            },
            {
                name: "name",
                value: (() => {
                    const personalData = getPersonalData();
                    // Si es empresa, mostrar "Nombre Apellido - Razón Social"
                    if (
                        personalData?.type === "empresa" ||
                        (personalData?.company_name && personalData.company_name.trim() !== "")
                    ) {
                        const fullName = personalData?.full_name || "";
                        const companyName = personalData?.company_name || "";
                        if (fullName && companyName) {
                            return `${fullName} - ${companyName}`;
                        }
                        return companyName || fullName || "";
                    }
                    // Si es persona, usar solo full_name
                    return personalData?.full_name || "";
                })(),
            },
        ];

        return leadData;
    };

    // Actualiza los datos de un lead existente con overrides opcionales
    const updateLead = (overrides = [], cb = () => {}, error_cb = () => {}) => {
        const current = stateRef.current;
        let leadData = getLeadData();
        const payment = leadData.find(item => item.name === "opcion-pago");

        // Aplicar overrides si se proporcionan
        if (overrides.length > 0) {
            overrides.forEach((override) => {
                leadData = leadData.map((item) => {
                    if (item.name === override.name) {
                        return override;
                    } else {
                        return item;
                    }
                });
            });
        }

        // Determinar categoría del lead basado en proceso de compra y contacto
        const procesoCompra = leadData.find((item) => item.name === "proceso-compra");
        const categoriaLead = leadData.find((item) => item.name === "categoria-lead");
        const requiereContacto = leadData.find((item) => item.name === "quiero-que-me-contacten");

        if (
            procesoCompra.value === getTimeframeOptions()[0].name &&
            requiereContacto.value === "Si"
        ) {
            categoriaLead.value = "hot";
        } else if (
            procesoCompra.value === getTimeframeOptions()[1].name &&
            requiereContacto.value === "Si"
        ) {
            categoriaLead.value = "warm";
        } else {
            categoriaLead.value = "normal";
        }

        const updateData = {
            id_lead: current.reservo.id_lead,
            data: leadData,
        };

        // Medio de contacto solicitado por el usuario (whatsapp/telefono/mail),
        // presente solo cuando pide ser contactado desde el modal
        const contactMethod =
            leadData.find(item => item.name === "quiero-que-me-contacten")?.value === "Si"
                ? leadData.find(item => item.name === "medio-de-contacto")?.value || null
                : null;

        // El producto Kinto depende del tipo de cliente del formulario inicial:
        // empresa genera un OneBusinessLead, persona natural un OnePersonalLead
        const isCompanyClient = isCompany();
        const kintoLeadType = isCompanyClient ? "one_business" : "one_personal";

        // Datos comunes a ambos productos Kinto
        const kintoLeadCommonData = {
            email: getPersonalData().email || "",
            phone_number: getPersonalData().phone_number || "",
            rut: cleanRutFormat(getPersonalData().rut),
            model: current.selectedVersion.kinto_car?.id || null,
            kms: leadData.find(item => item.name === "kilometraje-kinto")?.value || "",
            period: leadData.find(item => item.name === "cantidad-cuotas-kinto")?.value || "",
            purchase_timeframe: getSelectedTimeframe()
                ? getSelectedTimeframe().name
                : "Solo estoy cotizando",
            contact: contactMethod,
            city: selectedDealership?.city || "",
            station: null,
            station_text: selectedDealership?.name || "", // Texto simple de la estación
            source: "toyota", // Lead proveniente del cotizador de Toyota
        };

        const kintoLeadData = isCompanyClient
            ? {
                  ...kintoLeadCommonData,
                  company: getPersonalData().company_name || "",
                  // Enviar contact_name evita que el serializer mueva "contact"
                  // a este campo y pierda el medio de contacto solicitado
                  contact_name: getPersonalData().full_name || "",
                  // El configurador no pregunta el tamaño de la flota
                  fleet_size: "",
              }
            : {
                  ...kintoLeadCommonData,
                  full_name: getPersonalData().full_name || "",
                  company_name: getPersonalData().company_name || "",
              };

        // Verificar si es Kinto y si aún no se ha creado el lead de Kinto.
        // startsWith tolera leads antiguos con "kinto one personal/business"
        const isKintoPayment = Boolean(payment?.value?.startsWith("kinto"));
        const kintoLeadAlreadyCreated = current.kinto_lead_created || false;

        if (isKintoPayment && !kintoLeadAlreadyCreated && !creatingKintoLeadRef.current) {
            // Guard contra race condition y optimistic update
            creatingKintoLeadRef.current = true;
            setState((prevState) => ({
                ...prevState,
                kinto_lead_created: true,
            }));

            // Crear ambos leads solo si es la primera vez
            Promise.all([
                Axios.post(`/api/vehicles/bnp/leads/update_lead/`, updateData),
                Axios.post(`/api/kinto/forms/${kintoLeadType}/`, kintoLeadData)
            ])
            .then(([updateResponse, kintoLeadResponse]) => {
                setState((prevState) => ({
                    ...prevState,
                    kinto_lead_id: kintoLeadResponse.data.id, // Guardar el ID del lead de Kinto
                    kinto_lead_type: kintoLeadType, // Producto con el que se creó
                }));

                cb(updateResponse);
            })
            .catch((error) => {
                // Revertir optimistic update en caso de error
                creatingKintoLeadRef.current = false;
                setState((prevState) => ({
                    ...prevState,
                    kinto_lead_created: false,
                }));
                console.error("Error creating leads:", error);
                error_cb(error);
            });
        } else if (isKintoPayment && kintoLeadAlreadyCreated) {
            // Si es Kinto pero ya se creó el lead, actualizar el lead principal
            // y, si el usuario pidió ser contactado, propagar el contacto al lead de Kinto
            const requests = [
                Axios.post(`/api/vehicles/bnp/leads/update_lead/`, updateData),
            ];

            if (contactMethod && current.kinto_lead_id) {
                // Actualizar el endpoint del producto con el que se creó el lead
                const createdLeadType = current.kinto_lead_type || "one_personal";
                requests.push(
                    Axios.patch(
                        `/api/kinto/forms/${createdLeadType}_update/${current.kinto_lead_id}/`,
                        { contact: contactMethod }
                    )
                );
            }

            Promise.all(requests)
                .then(([response]) => {
                    console.log("Lead principal actualizado, lead de Kinto ya existe");
                    cb(response);
                })
                .catch(error_cb);
        } else {
            // Para otros tipos de pago, solo actualizar el lead principal
            Axios.post(`/api/vehicles/bnp/leads/update_lead/`, updateData)
                .then(cb)
                .catch(error_cb);
        }
    };



    // Crea un lead en Salesforce con los datos actuales
    const createSalesforceLead = (overrides = []) => {
        let leadData = [...getLeadData(), { name: "name", value: state.personalData.full_name }];

        if (overrides.length > 0) {
            overrides.forEach((override) => {
                leadData = leadData.map((item) => {
                    if (item.name === override.name) {
                        return override;
                    } else {
                        return item;
                    }
                });
            });
        }

        const procesoCompra = leadData.find((item) => item.name === "proceso-compra");
        const categoriaLead = leadData.find((item) => item.name === "categoria-lead");
        const requiereContacto = leadData.find((item) => item.name === "quiero-que-me-contacten");

        if (
            procesoCompra.value === getTimeframeOptions()[0].name &&
            requiereContacto.value === "Si"
        ) {
            categoriaLead.value = "hot";
        } else if (
            procesoCompra.value === getTimeframeOptions()[1].name &&
            requiereContacto.value === "Si"
        ) {
            categoriaLead.value = "warm";
        } else {
            categoriaLead.value = "normal";
        }

        const updateData = {
            id_lead: state.reservo.id_lead,
            data: leadData,
        };

        Axios.post(`/api/vehicles/bnp/leads/create_lead_salesforce/`, updateData).then(
            (response) => {
                if (response.status === 201) {
                    setState((prevState) => ({
                        ...prevState,
                        salesforce: {
                            ...prevState.salesforce, // Mantener las propiedades existentes
                            id_lead: response.data.id_lead_salesforce,
                        },
                    }));
                }
            }
        );
    };

    // Actualiza un lead existente en Salesforce
    const updateSalesforceLead = (overrides = []) => {
        let leadData = [...getLeadData(), { name: "name", value: state.personalData.full_name }];

        if (overrides.length > 0) {
            overrides.forEach((override) => {
                leadData = leadData.map((item) => {
                    if (item.name === override.name) {
                        return override;
                    } else {
                        return item;
                    }
                });
            });
        }

        const procesoCompra = leadData.find((item) => item.name === "proceso-compra");
        const categoriaLead = leadData.find((item) => item.name === "categoria-lead");
        const requiereContacto = leadData.find((item) => item.name === "quiero-que-me-contacten");

        if (
            procesoCompra.value === getTimeframeOptions()[0].name &&
            requiereContacto.value === "Si"
        ) {
            categoriaLead.value = "hot";
        } else if (
            procesoCompra.value === getTimeframeOptions()[1].name &&
            requiereContacto.value === "Si"
        ) {
            categoriaLead.value = "warm";
        } else {
            categoriaLead.value = "normal";
        }

        const updateData = {
            id_lead_salesforce: state.salesforce.id_lead,
            data: leadData,
        };

        Axios.post(`/api/vehicles/bnp/leads/update_lead_salesforce/`, updateData);
    };

    // Obtiene el ID del lead principal
    const getLeadId = () => {
        return state.reservo.id_lead;
    };

    // Obtiene el ID del lead en Salesforce
    const getSalesforceLeadId = () => {
        return state.salesforce.id_lead;
    };

    // Resetea el estado del lead de Kinto (útil para nuevas cotizaciones)
    const resetKintoLeadState = () => {
        setState((prevState) => ({
            ...prevState,
            kinto_lead_created: false,
            kinto_lead_id: null,
            kinto_lead_type: null,
        }));
    };

    // Verifica si ya se creó un lead de Kinto
    const isKintoLeadCreated = () => {
        return state.kinto_lead_created || false;
    };

    // Envía email con resumen de financiamiento
    const sendFinancingSummaryEmail = (callback, erroCallback) => {
        // Obtener todos los datos del lead igual que en updateLead
        const leadData = getLeadData();

        // Mismo snapshot que usa getLeadData, para que el email y el lead no
        // puedan mostrar cifras distintas
        const current = stateRef.current;
        const selectedVersion = current.selectedVersion;
        const kintoSimData = current.kintoSimulationData;
        const mafData = current.mafSimulationData;

        // Convertir el array de leadData a un objeto con propiedades
        const leadDataObject = {};
        leadData.forEach(item => {
            leadDataObject[item.name] = item.value;
        });
        
        // Calcular kinto_fee_value desde resultData de Kinto
        let kinto_fee_value = null;
        if (kintoSimData?.resultData?.amortization && kintoSimData.resultData.amortization.length > 0) {
            const lastAmortization = kintoSimData.resultData.amortization[kintoSimData.resultData.amortization.length - 1];
            kinto_fee_value = lastAmortization.totalQuota || null;
        }

        // Preparar datos para el email de resumen con todos los datos de leadData
        const personalData = getPersonalData();
        let data = {
            // Datos personales (con RUT limpio)
            ...personalData,
            rut: cleanRutFormat(personalData.rut),

            // Identificador del lead
            lead_id: getLeadId() || null,
            
            // IDs necesarios para el backend
            version_id: selectedVersion.id,
            selected_color_id: current.details.selectedColor?.id || null,
            selected_prepaid_maintenance_plan_id: current.selectedPrepaidMaintenancePlan?.id,
            dealership_id: selectedDealership.id,

            // Datos básicos del vehículo
            financed_total: computeFinancedTotal(current),
            price_with_bonus: getPriceWithBonusOnly(),
            main_features: selectedVersion?.main_features
                .map((feature) => ({
                    description: feature.description,
                    icon: feature.icon?.url || null
                })),
            
            // Accesorios
            added_accessories: current.accessories.added || [],
            total_price_accesories: current.accessories.totalPrice || null,

            // Auto usado
            usado: current.usado.range ? current.usado : null,
            
            // Tipo de financiamiento
            financing_type: leadDataObject["opcion-pago"],
            
            // Todos los datos del lead para debugging y procesamiento
            lead_data: leadDataObject,
            
            // Campos específicos de financiamiento tradicional (MAF).
            // Se toman del leadData ya validado: si la simulación no está
            // vigente ahí vienen en null y el email no muestra una cuota
            // que no corresponde a la configuración enviada.
            downpayment_amount: leadDataObject["cuota-mensual"]
                ? mafData?.downpayment_amount || null
                : null,
            credit_total_cost: leadDataObject["costo-total-credito"],
            equivalent_annual_load: leadDataObject["cae"],
            fee_value: leadDataObject["cuota-mensual"],
            smart_buy: leadDataObject["plan-renueve"] === "Si",
            future_value_guaranteed: leadDataObject["cuota-final"],
            number_fees: leadDataObject["cantidad-cuotas"],
            downpayment_percentage: leadDataObject["porcentaje-pie"],
            
            // Campos de Kinto
            kinto_downpayment_percentage: leadDataObject["porcentaje-pie-kinto"],
            kinto_number_fees: leadDataObject["cantidad-cuotas-kinto"],
            kinto_kilometers: leadDataObject["kilometraje-kinto"],
            kinto_fee_value: kinto_fee_value,
            
            // Otros campos del lead
            model_name: leadDataObject["modelo-texto"],
            version_name: leadDataObject["version-texto"],
            version_sap_id: leadDataObject["codigo-sap-vehiculo"],
            color_name: leadDataObject["color"],
            color_sap_id: leadDataObject["codigo-sap-color-vehiculo"],
            list_price: leadDataObject["precio-de-lista"],
            price: leadDataObject["precio"],
            final_price: leadDataObject["precio-final"],
            accessories_text: leadDataObject["accesorios"],
            accessories_sap_codes: leadDataObject["codigo-sap-accesorios-precio"],
            prepaid_maintenance_text: leadDataObject["mantenciones-prepagadas"],
            prepaid_maintenance_sap: leadDataObject["codigo-sap-mantencion-precio"],
            contact_request: leadDataObject["quiero-que-me-contacten"],
            contact_method: leadDataObject["medio-de-contacto"],
            used_car_text: leadDataObject["auto-usado"],
            purchase_process: leadDataObject["proceso-compra"],
            lead_category: leadDataObject["categoria-lead"],
            approval_result: leadDataObject["resultado-preaprobacion"],
            approval_info: leadDataObject["informacion_preaprobacion"],
            type: leadDataObject["type"],
            name: leadDataObject["name"],
        };

        Axios.post(`/api/vehicles/bnp/financing_summary/`, data).then(callback).catch(erroCallback);
    };

    // Retorna todas las funciones públicas del hook
    return {
        createFirstStepLead,
        createFirstStepLeadIfEnabled,
        shouldUseFirstStepLead,
        getAppVersion,
        createLead,
        updateLead,
        getLeadId,
        createOutOfStockLead,
        sendFinancingSummaryEmail,
        createSalesforceLead,
        updateSalesforceLead,
        getSalesforceLeadId,
        resetKintoLeadState,
        isKintoLeadCreated,
    };
};

export default useLeadData;
